import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Bell, Settings2, Clock, Mail, Info, Plus, Trash2, Loader2, Pencil, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/utils/axiosInstance';
import AlertFormDialog from '../components/AlertFormDialog';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

const COUNTRY_LABEL = {
  BE: '🇧🇪', NL: '🇳🇱', FR: '🇫🇷', DE: '🇩🇪', UK: '🇬🇧',
};

const Alerts = () => {
  const { t } = useTranslation('alerts');
  const { user } = useAuth();
  const { toast } = useToast();

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/alerts/');
      setAlerts(data.results || []);
    } catch (err) {
      toast({
        title: t('errors.fetchTitle'),
        description: t('errors.fetchDescription'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [user?.brand]);

  const handleToggle = async (alert) => {
    setTogglingId(alert.id);
    try {
      const { data } = await axiosInstance.patch(`/api/alerts/${alert.id}/update/`, {
        is_active: !alert.is_active,
      });
      setAlerts(prev => prev.map(a => (a.id === alert.id ? data : a)));
    } catch (err) {
      toast({
        title: t('errors.updateTitle'),
        description: t('errors.updateDescription'),
        variant: 'destructive',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const alert = confirmDelete;
    setDeletingId(alert.id);
    try {
      await axiosInstance.delete(`/api/alerts/${alert.id}/delete/`);
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
      toast({ title: t('deleted') });
      setConfirmDelete(null);
    } catch (err) {
      toast({
        title: t('errors.deleteTitle'),
        description: t('errors.deleteDescription'),
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (alert) => {
    setEditingId(alert.id);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    fetchAlerts();
  };

  const steps = [
    { icon: Settings2, key: 'step1' },
    { icon: Clock, key: 'step2' },
    { icon: Mail, key: 'step3' },
  ];

  return (
    <Layout>
      <PageHeader title={
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">{t('title')}</h1>
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
            {t('beta')}
          </Badge>
        </div>
      } />

      <div className="space-y-6">
        {/* Intro */}
        <p className="text-lg text-muted-foreground leading-relaxed">
          {t('intro.lead')}
        </p>

        {/* How it works */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">
            {t('howItWorks.title')}
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {steps.map(({ icon: Icon, key }, index) => (
              <Card key={key} className="border-amber-100">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground">
                    {t(`howItWorks.${key}.title`)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(`howItWorks.${key}.description`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Email notice */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Info className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900 leading-relaxed">
            {t('emailNotice')}
          </p>
        </div>

        {/* List + create button */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              {t('yourAlerts')}
            </h2>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('newAlert')}
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : alerts.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-2">
                <Bell className="h-10 w-10 mx-auto text-muted-foreground" />
                <p className="text-base font-medium text-foreground">
                  {t('emptyTitle')}
                </p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('emptyDescription')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alerts.map(alert => (
                <Card key={alert.id} className={alert.is_active ? '' : 'opacity-60'}>
                  <CardContent className="py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground truncate">
                          {alert.name}
                        </h3>
                        <Badge variant="secondary" className="font-mono">
                          {parseFloat(alert.threshold_pct).toFixed(2)}%
                        </Badge>
                        {alert.countries.length === 0 ? (
                          <Badge variant="outline" className="text-xs">
                            {t('allCountries')}
                          </Badge>
                        ) : (
                          <span className="text-sm">
                            {alert.countries.map(c => COUNTRY_LABEL[c] || c).join(' ')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('productsCount', { count: alert.products_count })}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        checked={alert.is_active}
                        disabled={togglingId === alert.id}
                        onCheckedChange={() => handleToggle(alert)}
                      />
                      <Link to={`/alerts/${alert.id}`}>
                        <Button variant="ghost" size="icon">
                          <History className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(alert)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deletingId === alert.id}
                        onClick={() => setConfirmDelete(alert)}
                      >
                        {deletingId === alert.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        alertId={editingId}
        onSaved={handleSaved}
      />

      <ConfirmDeleteModal
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        onConfirm={handleDeleteConfirmed}
        title={t('confirmDeleteTitle')}
        description={confirmDelete ? t('confirmDelete', { name: confirmDelete.name }) : ''}
        confirmText={t('confirmDeleteConfirm')}
        cancelText={t('confirmDeleteCancel')}
        loading={deletingId === confirmDelete?.id}
      />
    </Layout>
  );
};

export default Alerts;
